import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { StarOff, TrendingDown, ChevronLeft, ChevronRight } from "lucide-react";
import { Reveal } from "@/components/effects/Effects";

const CZ_DAYS = ["Ne", "Po", "Út", "St", "Čt", "Pá", "So"];
function fmtDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return `${CZ_DAYS[d.getDay()]} ${d.getDate()}.${d.getMonth() + 1}.`;
}

export function NegStars({ value, size = "w-4 h-4" }) {
  const n = Math.abs(value || 0);
  return (
    <span className="inline-flex items-center gap-0.5" data-testid="neg-stars">
      {[1, 2, 3, 4, 5].map((i) => (
        <StarOff key={i} className={`${size} ${i <= n ? "text-nv drop-shadow-[0_0_4px_rgba(74,222,128,0.5)]" : "text-neutral-800"}`} strokeWidth={2} />
      ))}
      <span className="ml-1.5 text-xs font-head text-nv tracking-widest">−{n}</span>
    </span>
  );
}

export default function Reviews2() {
  const [data, setData] = useState({ reviews: [], title: "", kicker: "", intro: "" });
  const [page, setPage] = useState(0);

  useEffect(() => {
    api.get("/reviews2/public").then(({ data }) => { setData(data); setPage(0); }).catch(() => {});
  }, []);

  const perPage = Math.max(1, data.per_page || 3);
  const reviews = data.reviews || [];
  const totalPages = Math.ceil(reviews.length / perPage) || 1;
  const safePage = Math.min(Math.max(0, page), totalPages - 1);
  const pageReviews = reviews.slice(safePage * perPage, safePage * perPage + perPage);

  return (
    <section id="negativni-recenze" className="relative bg-[#080706] border-b border-white/10" data-testid="reviews2-section">
      <div className="max-w-4xl mx-auto px-5 py-20 lg:py-28">
        <Reveal>
          {data.kicker ? <p className="text-xs tracking-[0.4em] text-nv uppercase mb-3">{data.kicker}</p> : null}
          <h2 className="font-head text-3xl sm:text-4xl text-neutral-100 mb-3 vhs-glitch">{data.title || "Negativní recenze"}</h2>
          {data.intro ? <p className="text-sm text-neutral-400 mb-6 max-w-2xl leading-relaxed whitespace-pre-line">{data.intro}</p> : <div className="mb-6" />}

          <div className="mb-12 border border-nv/30 bg-nv/[0.04] p-4 sm:p-5" data-testid="reviews2-scale-legend">
            <div className="flex items-center gap-2 mb-3">
              <TrendingDown className="w-4 h-4 text-nv" />
              <span className="text-xs tracking-[0.3em] text-nv uppercase font-head">Obrácená stupnice · čím víc mínus, tím líp</span>
            </div>
            <div className="flex flex-wrap items-end gap-x-4 gap-y-3">
              {[1, 2, 3, 4, 5].map((n) => (
                <div key={n} className="flex flex-col items-center gap-1" data-testid={`reviews2-scale-${n}`}>
                  <span className="inline-flex items-center gap-0.5">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <StarOff key={i} className={`w-3.5 h-3.5 sm:w-4 sm:h-4 ${i <= n ? "text-nv drop-shadow-[0_0_4px_rgba(74,222,128,0.5)]" : "text-neutral-800"}`} strokeWidth={2} />
                    ))}
                  </span>
                  <span className="text-xs font-head text-nv tracking-widest">−{n}</span>
                </div>
              ))}
            </div>
            <p className="mt-3 text-[11px] text-neutral-500">Hodnotí se od <span className="text-nv">−1</span> do <span className="text-nv">−5</span> hvězd.</p>
          </div>
        </Reveal>

        {data.reviews && data.reviews.length > 0 ? (
          <>
          <div className="space-y-4" data-testid="reviews2-list">
            {pageReviews.map((r, i) => (
              <Reveal key={`${safePage}-${i}`} delay={i * 60}>
                <div className="border border-white/10 bg-[#0b0a09] p-5 hover:border-nv/30 transition-colors" data-testid={`reviews2-item-${i}`}>
                  <div className="flex items-center justify-between gap-4 flex-wrap mb-2">
                    <span className="font-head text-base text-neutral-200">{r.nickname || "Anonym"}</span>
                    <NegStars value={r.stars} />
                  </div>
                  {r.text ? <p className="text-neutral-300 italic leading-relaxed">„{r.text}"</p> : null}
                  <p className="mt-2 text-[11px] text-neutral-600 tracking-widest">{fmtDate(r.created_at)}</p>
                  {r.reply ? (
                    <div className="mt-4 border-l-2 border-nv/40 pl-4 py-1 bg-black/30">
                      <p className="text-[11px] tracking-[0.3em] text-nv uppercase mb-1">Odpověď GATE ESCAPE</p>
                      <p className="text-sm text-neutral-400 whitespace-pre-line">{r.reply}</p>
                    </div>
                  ) : null}
                </div>
              </Reveal>
            ))}
          </div>
          {totalPages > 1 && (
            <div className="mt-10 flex items-center justify-center gap-5" data-testid="reviews2-pagination">
              <button onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={safePage === 0} data-testid="reviews2-prev" aria-label="Předchozí recenze" className="w-11 h-11 flex items-center justify-center border border-nv/40 text-nv hover:bg-nv/10 disabled:opacity-25 transition-colors"><ChevronLeft className="w-5 h-5" /></button>
              <span className="text-sm font-head text-nv tracking-widest" data-testid="reviews2-page-indicator">{safePage + 1} / {totalPages}</span>
              <button onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))} disabled={safePage >= totalPages - 1} data-testid="reviews2-next" aria-label="Další recenze" className="w-11 h-11 flex items-center justify-center border border-nv/40 text-nv hover:bg-nv/10 disabled:opacity-25 transition-colors"><ChevronRight className="w-5 h-5" /></button>
            </div>
          )}
          </>
        ) : (
          <Reveal>
            <div className="text-center border border-dashed border-white/10 py-10 px-6" data-testid="reviews2-empty">
              <p className="font-head text-xl text-neutral-300 mb-2">Zatím žádné negativní recenze.</p>
              <p className="text-sm text-neutral-500">Buď první, kdo nám dá pořádně mínus hvězd.</p>
            </div>
          </Reveal>
        )}
      </div>
    </section>
  );
}
